// TerrainAdvisorBar \u2014 bottom-of-map control surface for the Terrain
// Advisor (TAWS) overlay. ForeFlight has the same strip: shows the
// active hazard altitudes + color swatches + a settings gear.
//
// Only renders when the terrain layer is on. Stacks above the
// bottom instrument strip via the shared useInstrumentStripHeight()
// hook, same pattern as NexradBar / SfcAnalysisBar.

import { useState } from 'react';
import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import { useInstrumentPrefsStore } from '../stores/instrumentPrefsStore';
import { useTerrainAdvisorStore, TERRAIN_PRESETS } from '../stores/terrainAdvisorStore';
import { useInstrumentStripHeight } from './useInstrumentStripHeight';
import TerrainAdvisorSettings from './TerrainAdvisorSettings';

export default function TerrainAdvisorBar() {
  const showTerrain    = useLayerVisibilityStore((s) => s.layers.terrain);
  const showInstruments = useInstrumentPrefsStore((s) => s.showInstruments);
  const showNexrad     = useLayerVisibilityStore((s) => s.layers.nexrad);
  const showWxHazards  = useLayerVisibilityStore((s) => s.layers.wxhazards);
  const showSfc        = useLayerVisibilityStore((s) => s.layers.sfcanalysis);

  const presetId        = useTerrainAdvisorStore((s) => s.presetId);
  const redFt           = useTerrainAdvisorStore((s) => s.redFt);
  const yellowFt        = useTerrainAdvisorStore((s) => s.yellowFt);
  const corridorMode    = useTerrainAdvisorStore((s) => s.corridorMode);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const instrumentH = useInstrumentStripHeight(showTerrain);

  if (!showTerrain) return null;

  // Stack vertically above whichever other bars are visible. Same
  // bottom-offset arithmetic as NexradBar. Each bar contributes a
  // rough footprint in px so we don't overlap.
  const INSTRUMENT_BOTTOM_OFFSET = 12;
  const ABOVE_INSTRUMENTS_GAP_PX = 14;
  const HAZARD_BAR_FOOTPRINT_PX = 110;
  const NEXRAD_BAR_FOOTPRINT_PX = 64;
  const SFC_BAR_FOOTPRINT_PX = 64;
  let bottomPx = showInstruments
    ? INSTRUMENT_BOTTOM_OFFSET + instrumentH + ABOVE_INSTRUMENTS_GAP_PX
    : 14;
  if (showWxHazards) bottomPx += HAZARD_BAR_FOOTPRINT_PX;
  if (showNexrad)    bottomPx += NEXRAD_BAR_FOOTPRINT_PX;
  if (showSfc)       bottomPx += SFC_BAR_FOOTPRINT_PX;

  const presetLabel = presetId === 'custom'
    ? 'Custom'
    : TERRAIN_PRESETS[presetId]?.label ?? '\u2014';

  const corridorLabel =
    corridorMode === 'auto' ? 'Corridor' :
    corridorMode === 'show' ? 'All map'   :
    'Hidden';

  return (
    <>
      <div
        className="absolute left-0 right-0 z-10 flex justify-center pointer-events-none"
        style={{ bottom: `${bottomPx}px` }}
      >
        {/* Compact pill — sized to content, never edge-to-edge on mobile.
           On phones the corridor mode summary is dropped (visible in
           the settings sheet); on tablet+ it appears. */}
        <div className="pointer-events-auto bg-cockpit-800/95 backdrop-blur border border-cockpit-600 rounded-xl shadow-bar px-2.5 py-1.5 max-w-[min(22rem,calc(100vw-1rem))]">
          <div className="flex items-center gap-2.5">
            <div className="flex flex-col items-start">
              <span className="text-[9px] tracking-widest text-cockpit-400 uppercase leading-none">
                Terrain
              </span>
              <span className="font-mono text-[11px] text-ember-300 leading-tight mt-0.5 whitespace-nowrap">
                {presetLabel}
              </span>
            </div>

            {/* Color swatches + thresholds — compact, no center justify. */}
            <div className="flex items-center gap-2 text-[11px]">
              <div className="flex items-center gap-1">
                <span
                  className="inline-block w-3 h-3 rounded-sm border border-cockpit-600"
                  style={{ background: 'rgb(217,25,25)' }}
                  aria-label="Red threshold"
                />
                <span className="font-mono text-cockpit-200">&lt;{redFt}'</span>
              </div>
              <div className="flex items-center gap-1">
                <span
                  className="inline-block w-3 h-3 rounded-sm border border-cockpit-600"
                  style={{ background: 'rgb(250,184,26)' }}
                  aria-label="Yellow threshold"
                />
                <span className="font-mono text-cockpit-200">&lt;{yellowFt}'</span>
              </div>
              <span className="text-[10px] text-cockpit-400 hidden md:inline">
                {corridorLabel}
              </span>
            </div>

            <button
              onClick={() => setSettingsOpen(true)}
              className="text-cockpit-300 hover:text-cockpit-100 p-1 -m-1"
              aria-label="Terrain advisor settings"
              title="Terrain advisor settings"
            >
              {/* Gear icon \u2014 simple SVG, matches other icons in the bar */}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9 1.65 1.65 0 0 0 4.27 7.18l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {settingsOpen && (
        <TerrainAdvisorSettings onClose={() => setSettingsOpen(false)} />
      )}
    </>
  );
}
